// frontend/src/pages/Dashboard.jsx
// Landing page shown to all users after login
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPoles, fetchProximityLogs } from "../services/api";

const ROLE_CONFIG = {
  admin:      { icon:"🔑", label:"Administrator", color:"#f59e0b" },
  supervisor: { icon:"🦺", label:"Supervisor",    color:"#22c55e" },
  technician: { icon:"🔧", label:"Technician",    color:"#3b82f6" },
  viewer:     { icon:"👁",  label:"Viewer",        color:"#6b7280" },
};

function StatCard({ icon, label, value, color="#22c55e", onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:"#0d1520", border:`1px solid ${color}30`, borderRadius:14, padding:"20px 24px",
        cursor: onClick ? "pointer" : "default", transition:"all 0.2s",
        borderLeft:`3px solid ${color}` }}>
      <div style={{ fontSize:28, marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:28, fontWeight:800, color, fontFamily:"'Syne',sans-serif" }}>{value}</div>
      <div style={{ fontSize:12, color:"#64748b", marginTop:4 }}>{label}</div>
    </div>
  );
}

function QuickLink({ icon, label, desc, color="#22c55e", onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:"#0d1520", border:`1px solid ${color}30`, borderRadius:14, padding:"18px 20px",
        cursor:"pointer", transition:"all 0.2s", display:"flex", alignItems:"center", gap:14 }}>
      <div style={{ width:44, height:44, borderRadius:12, background:`${color}18`, border:`1px solid ${color}30`,
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0" }}>{label}</div>
        <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{desc}</div>
      </div>
      <div style={{ marginLeft:"auto", color:`${color}80`, fontSize:18 }}>›</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate    = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const role        = currentUser.role || "viewer";
  const roleConf    = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const isAdmin     = role === "admin";

  const [poles, setPoles]   = useState([]);
  const [logs, setLogs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [time, setTime]     = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    Promise.all([
      fetchPoles().then(r => setPoles(r.data.data)),
      fetchProximityLogs(null, 5).then(r => setLogs(r.data.data || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const activePoles      = poles.filter(p => p.status === "Active").length;
  const maintenancePoles = poles.filter(p => p.status === "Maintenance").length;
  const pendingPoles     = poles.filter(p => p.status === "Pending").length;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const greetingHour = time.getHours();
  const greeting = greetingHour < 12 ? "Good Morning" : greetingHour < 17 ? "Good Afternoon" : "Good Evening";

  return (
    <div style={{ minHeight:"100vh", background:"#0a0f1a", fontFamily:"'JetBrains Mono',monospace", color:"#e2e8f0" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .fade-in { animation: fadeIn 0.4s ease forwards; }
        * { box-sizing: border-box; }
        @media (max-width: 600px) {
          .stat-grid { grid-template-columns: 1fr 1fr !important; }
          .link-grid { grid-template-columns: 1fr !important; }
          .top-nav   { flex-wrap: wrap; gap: 8px !important; }
        }
      `}</style>

      {/* ── Top Nav ── */}
      <div className="top-nav" style={{ background:"linear-gradient(135deg,#0d2010,#0a1628)", borderBottom:"1px solid #1a2e1a",
        padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ fontSize:24 }}>⚡</div>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:16, fontWeight:800, color:"#4ade80" }}>CEB Solar Poles</div>
            <div style={{ fontSize:9, color:"#4b5563", letterSpacing:"0.12em" }}>FIELD SUPERVISORY SYSTEM</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ fontSize:11, color:"#64748b", textAlign:"right" }}>
            <div style={{ color:"#94a3b8" }}>{currentUser.full_name}</div>
            <div style={{ color: roleConf.color, fontSize:10 }}>{roleConf.icon} {roleConf.label}</div>
          </div>
          <button onClick={handleLogout}
            style={{ background:"#450a0a", border:"1px solid #7f1d1d", color:"#f87171", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
            LOGOUT
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"24px 16px" }}>

        {/* Greeting */}
        <div className="fade-in" style={{ marginBottom:24 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:22, fontWeight:800, color:"#e2e8f0" }}>
            {greeting}, {currentUser.full_name?.split(" ")[0]}! 👋
          </div>
          <div style={{ fontSize:12, color:"#4b5563", marginTop:4 }}>
            {time.toLocaleDateString("en-LK", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}
            {" · "}
            {time.toLocaleTimeString("en-LK", { hour:"2-digit", minute:"2-digit", second:"2-digit" })}
          </div>
        </div>

        {/* Stats */}
        <div className="fade-in stat-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
          <StatCard icon="🔆" label="Total Poles"       value={loading ? "..." : poles.length}      color="#4ade80" />
          <StatCard icon="✅" label="Active"            value={loading ? "..." : activePoles}        color="#22c55e" />
          <StatCard icon="🔧" label="Maintenance"       value={loading ? "..." : maintenancePoles}   color="#f59e0b" />
          <StatCard icon="⏳" label="Pending"           value={loading ? "..." : pendingPoles}       color="#3b82f6" />
        </div>

        {/* Quick Links */}
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#4b5563", letterSpacing:"0.1em", marginBottom:12 }}>QUICK ACCESS</div>
          <div className="link-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <QuickLink icon="🗺️" label="Live Map"          desc="View poles & track GPS location"   color="#22c55e" onClick={() => navigate("/")} />
            <QuickLink icon="📊" label="Reports"           desc="Proximity logs, charts & exports"  color="#3b82f6" onClick={() => navigate("/reports")} />
            {isAdmin && <QuickLink icon="⚙️" label="Admin Dashboard" desc="Users, roles & groups"          color="#f59e0b" onClick={() => navigate("/admin")} />}
            {["admin","supervisor"].includes(role) && <QuickLink icon="📍" label="Collect GPS Pole"  desc="Tap map to register new pole"    color="#8b5cf6" onClick={() => navigate("/collect")} />}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0" }}>📋 Recent Proximity Activity</div>
            <button onClick={() => navigate("/reports")}
              style={{ background:"transparent", border:"1px solid #1e3a2e", color:"#4ade80", borderRadius:8, padding:"4px 10px", fontSize:10, cursor:"pointer" }}>
              View All →
            </button>
          </div>
          {loading ? (
            <div style={{ color:"#4b5563", fontSize:12, textAlign:"center", padding:20 }}>Loading...</div>
          ) : logs.length === 0 ? (
            <div style={{ color:"#4b5563", fontSize:12, textAlign:"center", padding:20 }}>No activity yet — go visit some poles! 😊</div>
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr>
                  {["Pole","User","Distance","Time"].map(h => (
                    <th key={h} style={{ fontSize:10, color:"#4b5563", textAlign:"left", padding:"6px 10px", borderBottom:"1px solid #1a2e1a", letterSpacing:"0.08em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i}>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#4ade80", fontWeight:600 }}>{log.pole_name || log.pole_id}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#94a3b8" }}>{log.username || "—"}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#e2e8f0" }}>{log.distance_m ? `${parseFloat(log.distance_m).toFixed(1)}m` : "—"}</td>
                    <td style={{ padding:"8px 10px", fontSize:11, color:"#4b5563" }}>
                      {log.logged_at ? new Date(log.logged_at).toLocaleString("en-LK", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pole Status Summary */}
        <div style={{ marginTop:16, background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:14 }}>🔆 Pole Status Overview</div>
          {loading ? (
            <div style={{ color:"#4b5563", fontSize:12, textAlign:"center", padding:20 }}>Loading...</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {poles.map(pole => {
                const colors = { Active:"#22c55e", Maintenance:"#f59e0b", Inactive:"#6b7280", Pending:"#3b82f6" };
                const color  = colors[pole.status] || "#22c55e";
                return (
                  <div key={pole.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", borderRadius:8, background:"rgba(255,255,255,0.02)" }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:color, flexShrink:0, display:"inline-block" }} />
                    <span style={{ fontSize:12, fontWeight:600, color:"#e2e8f0", flex:1 }}>{pole.name}</span>
                    <span style={{ fontSize:10, color:"#64748b" }}>{pole.pole_code}</span>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:99, border:`1px solid ${color}40`, background:`${color}18`, color }}>{pole.status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
